import { CognitoGroup } from '@auth/groups';
import { useQuery } from '@tanstack/react-query';
import { UtilityId } from '@utilities/types';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useMemo } from 'react';

/**
 * Per-utility Cognito group rules (groups come from the ID token's
 * `cognito:groups` claim). Each rule is satisfied by membership in at least
 * one listed group.
 *  - `access`: who can see/open the utility at all.
 *  - `actions`: who can perform a named action inside it. UX gating only —
 *    the API remains the source of truth.
 */
const UTILITY_PERMISSIONS = {
  [UtilityId.AccMassUpload]: {
    access: [CognitoGroup.AccEditor, CognitoGroup.AccViewer],
    actions: {
      upload: [CognitoGroup.AccEditor],
    },
  },
  [UtilityId.ManagePermissions]: {
    access: [CognitoGroup.HubAdmin],
    actions: {
      manage: [CognitoGroup.HubAdmin],
    },
  },
} as const satisfies Record<UtilityId, { access: readonly string[]; actions: Record<string, readonly string[]> }>;

type UtilityAction<U extends UtilityId> = keyof (typeof UTILITY_PERMISSIONS)[U]['actions'];

/** Is there at least one matching group from the allowedGroups that the userGroup includes? */
const isInAnyGroup = (userGroups: readonly string[], allowedGroups: readonly string[]) =>
  allowedGroups.some(group => userGroups.includes(group));

const getUserGroups = async (): Promise<string[]> => {
  const { tokens } = await fetchAuthSession();
  const groupsClaim = tokens?.idToken?.payload['cognito:groups'];
  return Array.isArray(groupsClaim) ? groupsClaim.map(String) : [];
};

interface UsePermissionsResponse {
  /** Utility Ids for which the user has at least one group from the valid groups for access. */
  grantedUtilityIds: UtilityId[];
  /** Whether the user can open the passed Utility by having at least one valid access group for the Utility. */
  hasAccess: (utilityId: UtilityId) => boolean;
  /** Whether the user can perform the specified action within the passed Utility. False while loading. */
  hasPermission: <U extends UtilityId>(utilityId: U, action: UtilityAction<U>) => boolean;
  /** Whether the permissions are still being loaded. */
  isLoading: boolean;
}

export const usePermissions = (): UsePermissionsResponse => {
  const { data: userGroups, isLoading } = useQuery({
    queryFn: getUserGroups,
    queryKey: ['permissions'],
  });

  const grantedUtilityIds = useMemo(
    () =>
      (Object.keys(UTILITY_PERMISSIONS) as UtilityId[]).filter(utilityId =>
        isInAnyGroup(userGroups ?? [], UTILITY_PERMISSIONS[utilityId].access),
      ),
    [userGroups],
  );

  return {
    grantedUtilityIds,
    hasAccess: utilityId => grantedUtilityIds.includes(utilityId),
    hasPermission: (utilityId, action) => {
      const { actions }: { actions: Record<PropertyKey, readonly string[]> } = UTILITY_PERMISSIONS[utilityId];
      const { [action]: allowedGroups } = actions;
      return isInAnyGroup(userGroups ?? [], allowedGroups);
    },
    isLoading,
  };
};
